import {
  BulkGenerateFolderResult,
  editionsService,
} from '@/services/editions.service';
import { useEditionPresetsStore } from '@/store/edition-presets.store';
import { useMutation, useQuery } from '@tanstack/react-query';
import {
  AlertCircle,
  Bookmark,
  Check,
  CheckCircle2,
  CheckSquare,
  FileText,
  Loader2,
  Plus,
  Search,
  Square,
  X,
  XCircle,
} from 'lucide-react';
import { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { EDITION_OPTIONS } from '../constants/edition-options.constants';

interface BulkEditionsDrawerProps {
  isOpen: boolean;
  onClose: () => void;
  folderUuids: string[];
  folderNames: string[];
}

const MONTHS = [
  { value: '01', label: 'Janvier' },
  { value: '02', label: 'Février' },
  { value: '03', label: 'Mars' },
  { value: '04', label: 'Avril' },
  { value: '05', label: 'Mai' },
  { value: '06', label: 'Juin' },
  { value: '07', label: 'Juillet' },
  { value: '08', label: 'Août' },
  { value: '09', label: 'Septembre' },
  { value: '10', label: 'Octobre' },
  { value: '11', label: 'Novembre' },
  { value: '12', label: 'Décembre' },
];

const YEARS = Array.from({ length: 10 }, (_, i) => {
  const year = new Date().getFullYear() - 2 + i;
  return { value: year.toString(), label: year.toString() };
});

export function BulkEditionsDrawer({
  isOpen,
  onClose,
  folderUuids,
  folderNames,
}: BulkEditionsDrawerProps) {
  const { t } = useTranslation();
  const { presets, addPreset, removePreset } = useEditionPresetsStore();

  // Preset state
  const [isAddingPreset, setIsAddingPreset] = useState(false);
  const [presetName, setPresetName] = useState('');
  const [activePresetId, setActivePresetId] = useState<string | null>(null);
  const presetInputRef = useRef<HTMLInputElement>(null);

  // Edition selection state
  const [selectedEditions, setSelectedEditions] = useState<string[]>([]);
  const [editionSearch, setEditionSearch] = useState('');

  // Period state
  const [startMonth, setStartMonth] = useState(
    new Date().getMonth().toString().padStart(2, '0')
  );
  const [startYear, setStartYear] = useState(
    new Date().getFullYear().toString()
  );
  const [endMonth, setEndMonth] = useState(
    new Date().getMonth().toString().padStart(2, '0')
  );
  const [endYear, setEndYear] = useState(new Date().getFullYear().toString());

  // Progress state
  const [batchId, setBatchId] = useState<string | null>(null);
  const [isSubmitted, setIsSubmitted] = useState(false);

  // Filter editions by search
  const filteredEditions = EDITION_OPTIONS.filter(
    opt =>
      opt.name.toLowerCase().includes(editionSearch.toLowerCase()) ||
      opt.value.toLowerCase().includes(editionSearch.toLowerCase())
  );

  // Toggle edition selection
  const toggleEdition = (value: string) => {
    setSelectedEditions(prev =>
      prev.includes(value) ? prev.filter(v => v !== value) : [...prev, value]
    );
    setActivePresetId(null);
  };

  const selectAll = () => {
    setSelectedEditions(filteredEditions.map(e => e.value));
    setActivePresetId(null);
  };

  const deselectAll = () => {
    setSelectedEditions([]);
    setActivePresetId(null);
  };

  // Apply preset
  const applyPreset = (presetId: string) => {
    const preset = presets.find(p => p.id === presetId);
    if (!preset) return;
    setSelectedEditions(preset.editionValues);
    setActivePresetId(presetId);
  };

  // Save preset
  const handleSavePreset = () => {
    const name = presetName.trim();
    if (!name || selectedEditions.length === 0) return;
    addPreset(name, selectedEditions);
    setPresetName('');
    setIsAddingPreset(false);
  };

  // Submit mutation
  const bulkMutation = useMutation({
    mutationFn: () =>
      editionsService.bulkGenerate({
        folderUuids,
        editionValues: selectedEditions,
        startMonth: parseInt(startMonth),
        startYear: parseInt(startYear),
        endMonth: parseInt(endMonth),
        endYear: parseInt(endYear),
      }),
    onSuccess: response => {
      setBatchId(response.data.batchId);
      setIsSubmitted(true);
    },
  });

  // Poll progress
  const { data: progressData } = useQuery({
    queryKey: ['bulk-editions-progress', batchId],
    queryFn: () => editionsService.getBulkProgress(batchId!),
    enabled: !!batchId && isSubmitted,
    refetchInterval: query =>
      query.state.data?.data?.status === 'completed' ? false : 5000,
    retry: false,
  });

  const progress = progressData?.data;
  const progressPercent = progress
    ? Math.round((progress.completed / progress.total) * 100)
    : 0;
  const isCompleted = progress?.status === 'completed';

  // Handle close / reset
  const handleClose = () => {
    setSelectedEditions([]);
    setEditionSearch('');
    setBatchId(null);
    setIsSubmitted(false);
    setActivePresetId(null);
    onClose();
  };

  const handleSubmit = () => {
    if (selectedEditions.length === 0 || folderUuids.length === 0) return;
    bulkMutation.mutate();
  };

  const getStatusIcon = (result: BulkGenerateFolderResult) => {
    switch (result.status) {
      case 'completed':
        return <CheckCircle2 className="h-4 w-4 text-green-500" />;
      case 'failed':
        return <XCircle className="h-4 w-4 text-red-500" />;
      case 'processing':
        return <Loader2 className="h-4 w-4 animate-spin text-blue-500" />;
      default:
        return (
          <div className="h-4 w-4 rounded-full border-2 border-neutral-300" />
        );
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex">
      {/* Backdrop */}
      <div
        className="flex-1 bg-neutral-900/50 backdrop-blur-sm"
        onClick={handleClose}
      />

      {/* Drawer */}
      <div className="flex h-full w-full max-w-xl flex-col bg-white shadow-2xl">
        {/* Header */}
        <div className="flex items-center justify-between border-b border-neutral-200 p-6">
          <div className="flex items-center gap-3">
            <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-violet-100">
              <FileText className="h-5 w-5 text-violet-600" />
            </div>
            <div>
              <h2 className="text-lg font-bold text-neutral-900">
                {t('editions.bulk.title', "Génération d'éditions en masse")}
              </h2>
              <p className="text-sm text-neutral-500">
                {t(
                  'editions.bulk.subtitle',
                  '{{count}} dossier(s) sélectionné(s)',
                  {
                    count: folderUuids.length,
                  }
                )}
                {folderNames.length <= 3 && folderNames.length > 0 && (
                  <span className="mt-0.5 block truncate text-xs text-neutral-400">
                    {folderNames.join(', ')}
                  </span>
                )}
              </p>
            </div>
          </div>
          <button
            onClick={handleClose}
            className="rounded-lg p-2 text-neutral-400 transition-colors hover:bg-neutral-100 hover:text-neutral-600"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 space-y-6 overflow-y-auto p-6">
          {!isSubmitted ? (
            <>
              {/* Presets / Favoris */}
              {presets.length > 0 && (
                <div>
                  <h3 className="mb-2 text-sm font-semibold text-neutral-700">
                    {t('editions.presets.title', 'Favoris')}
                  </h3>
                  <div className="flex flex-wrap gap-2">
                    {presets.map(preset => (
                      <button
                        key={preset.id}
                        onClick={() => applyPreset(preset.id)}
                        className={`flex items-center gap-1.5 rounded-full border px-3 py-1.5 text-xs font-medium transition-colors ${
                          activePresetId === preset.id
                            ? 'border-violet-300 bg-violet-50 text-violet-700'
                            : 'border-neutral-200 bg-white text-neutral-600 hover:border-neutral-300'
                        }`}
                      >
                        <Bookmark className="h-3 w-3" />
                        {preset.name}
                        <span className="text-neutral-400">
                          ({preset.editionValues.length})
                        </span>
                        <button
                          onClick={e => {
                            e.stopPropagation();
                            removePreset(preset.id);
                            if (activePresetId === preset.id)
                              setActivePresetId(null);
                          }}
                          className="ml-1 rounded-full p-0.5 text-neutral-400 hover:bg-neutral-200 hover:text-neutral-600"
                        >
                          <X className="h-2.5 w-2.5" />
                        </button>
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {/* Save preset button */}
              {selectedEditions.length > 0 && (
                <div>
                  {isAddingPreset ? (
                    <div className="flex items-center gap-2">
                      <input
                        ref={presetInputRef}
                        type="text"
                        value={presetName}
                        onChange={e => setPresetName(e.target.value)}
                        onKeyDown={e => {
                          if (e.key === 'Enter') handleSavePreset();
                          if (e.key === 'Escape') {
                            setIsAddingPreset(false);
                            setPresetName('');
                          }
                        }}
                        placeholder={t(
                          'editions.presets.namePlaceholder',
                          'Nom du favori...'
                        )}
                        className="flex-1 rounded-lg border border-neutral-300 px-3 py-2 text-sm focus:border-violet-500 focus:ring-2 focus:ring-violet-100"
                        autoFocus
                      />
                      <button
                        onClick={handleSavePreset}
                        disabled={!presetName.trim()}
                        className="rounded-lg bg-violet-600 px-3 py-2 text-sm font-medium text-white hover:bg-violet-700 disabled:opacity-50"
                      >
                        {t('common.save', 'Enregistrer')}
                      </button>
                      <button
                        onClick={() => {
                          setIsAddingPreset(false);
                          setPresetName('');
                        }}
                        className="rounded-lg border border-neutral-300 px-3 py-2 text-sm text-neutral-600 hover:bg-neutral-50"
                      >
                        <X className="h-4 w-4" />
                      </button>
                    </div>
                  ) : (
                    <button
                      onClick={() => setIsAddingPreset(true)}
                      className="flex items-center gap-1.5 text-sm text-violet-600 hover:text-violet-700"
                    >
                      <Plus className="h-3.5 w-3.5" />
                      {t(
                        'editions.presets.save',
                        'Sauvegarder en tant que favori'
                      )}
                    </button>
                  )}
                </div>
              )}

              {/* Edition selection */}
              <div>
                <div className="mb-3 flex items-center justify-between">
                  <h3 className="text-sm font-semibold text-neutral-700">
                    {t(
                      'editions.bulk.selectEditions',
                      'Sélectionner les éditions'
                    )}
                    {selectedEditions.length > 0 && (
                      <span className="ml-2 text-violet-600">
                        ({selectedEditions.length})
                      </span>
                    )}
                  </h3>
                  <div className="flex items-center gap-2">
                    <button
                      onClick={selectAll}
                      className="text-xs text-violet-600 hover:text-violet-700"
                    >
                      {t('common.selectAll', 'Tout sélectionner')}
                    </button>
                    <span className="text-neutral-300">|</span>
                    <button
                      onClick={deselectAll}
                      className="text-xs text-neutral-500 hover:text-neutral-700"
                    >
                      {t('common.deselectAll', 'Tout désélectionner')}
                    </button>
                  </div>
                </div>

                {/* Search */}
                <div className="relative mb-3">
                  <Search className="absolute top-1/2 left-3 h-4 w-4 -translate-y-1/2 text-neutral-400" />
                  <input
                    type="text"
                    value={editionSearch}
                    onChange={e => setEditionSearch(e.target.value)}
                    placeholder={t(
                      'editions.bulk.searchPlaceholder',
                      'Rechercher une édition...'
                    )}
                    className="w-full rounded-lg border border-neutral-300 py-2 pr-4 pl-10 text-sm focus:border-violet-500 focus:ring-2 focus:ring-violet-100"
                  />
                </div>

                {/* Edition checkboxes */}
                <div className="max-h-60 space-y-1 overflow-y-auto rounded-lg border border-neutral-200 p-2">
                  {filteredEditions.map(option => {
                    const isChecked = selectedEditions.includes(option.value);
                    return (
                      <button
                        key={option.value}
                        onClick={() => toggleEdition(option.value)}
                        className={`flex w-full items-center gap-2.5 rounded-lg px-3 py-2 text-left text-sm transition-colors ${
                          isChecked
                            ? 'bg-violet-50 text-violet-700'
                            : 'text-neutral-700 hover:bg-neutral-50'
                        }`}
                      >
                        {isChecked ? (
                          <CheckSquare className="h-4 w-4 flex-shrink-0 text-violet-600" />
                        ) : (
                          <Square className="h-4 w-4 flex-shrink-0 text-neutral-400" />
                        )}
                        <span className="flex-1 truncate">{option.name}</span>
                        <span className="text-xs text-neutral-400">
                          {option.value}
                        </span>
                      </button>
                    );
                  })}
                  {filteredEditions.length === 0 && (
                    <p className="py-4 text-center text-sm text-neutral-500">
                      {t('editions.bulk.noResults', 'Aucune édition trouvée')}
                    </p>
                  )}
                </div>
              </div>

              {/* Period selectors */}
              <div>
                <h3 className="mb-3 text-sm font-semibold text-neutral-700">
                  {t('editions.period.title', 'Période')}
                </h3>
                <div className="grid grid-cols-2 gap-4">
                  {/* Start date */}
                  <div>
                    <label className="mb-1 block text-xs font-medium text-neutral-500">
                      {t('editions.period.start', 'Début')}
                    </label>
                    <div className="grid grid-cols-2 gap-2">
                      <select
                        value={startMonth}
                        onChange={e => setStartMonth(e.target.value)}
                        className="rounded-lg border border-neutral-300 px-2 py-2 text-sm focus:border-violet-500 focus:ring-2 focus:ring-violet-100"
                      >
                        {MONTHS.map(m => (
                          <option key={m.value} value={m.value}>
                            {m.label}
                          </option>
                        ))}
                      </select>
                      <select
                        value={startYear}
                        onChange={e => setStartYear(e.target.value)}
                        className="rounded-lg border border-neutral-300 px-2 py-2 text-sm focus:border-violet-500 focus:ring-2 focus:ring-violet-100"
                      >
                        {YEARS.map(y => (
                          <option key={y.value} value={y.value}>
                            {y.label}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>

                  {/* End date */}
                  <div>
                    <label className="mb-1 block text-xs font-medium text-neutral-500">
                      {t('editions.period.end', 'Fin')}
                    </label>
                    <div className="grid grid-cols-2 gap-2">
                      <select
                        value={endMonth}
                        onChange={e => setEndMonth(e.target.value)}
                        className="rounded-lg border border-neutral-300 px-2 py-2 text-sm focus:border-violet-500 focus:ring-2 focus:ring-violet-100"
                      >
                        {MONTHS.map(m => (
                          <option key={m.value} value={m.value}>
                            {m.label}
                          </option>
                        ))}
                      </select>
                      <select
                        value={endYear}
                        onChange={e => setEndYear(e.target.value)}
                        className="rounded-lg border border-neutral-300 px-2 py-2 text-sm focus:border-violet-500 focus:ring-2 focus:ring-violet-100"
                      >
                        {YEARS.map(y => (
                          <option key={y.value} value={y.value}>
                            {y.label}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>
                </div>
              </div>

              {/* Summary */}
              {selectedEditions.length > 0 && (
                <div className="rounded-lg border border-violet-200 bg-violet-50 p-4">
                  <h4 className="mb-2 text-sm font-semibold text-violet-800">
                    {t('editions.bulk.summary', 'Résumé')}
                  </h4>
                  <ul className="space-y-1 text-sm text-violet-700">
                    <li>
                      {t(
                        'editions.bulk.summaryFolders',
                        '{{count}} dossier(s)',
                        {
                          count: folderUuids.length,
                        }
                      )}
                    </li>
                    <li>
                      {t(
                        'editions.bulk.summaryEditions',
                        '{{count}} édition(s)',
                        {
                          count: selectedEditions.length,
                        }
                      )}
                    </li>
                    <li>
                      {t(
                        'editions.bulk.summaryPeriod',
                        'Période : {{start}} - {{end}}',
                        {
                          start: `${MONTHS.find(m => m.value === startMonth)?.label} ${startYear}`,
                          end: `${MONTHS.find(m => m.value === endMonth)?.label} ${endYear}`,
                        }
                      )}
                    </li>
                    <li className="font-medium">
                      {t(
                        'editions.bulk.summaryTotal',
                        'Total : {{total}} génération(s)',
                        {
                          total: folderUuids.length * selectedEditions.length,
                        }
                      )}
                    </li>
                  </ul>
                </div>
              )}
            </>
          ) : (
            /* Progress view */
            <div className="space-y-4">
              {/* Progress bar */}
              <div>
                <div className="mb-2 flex items-center justify-between text-sm">
                  <span className="font-medium text-neutral-700">
                    {isCompleted
                      ? t('editions.bulk.progress.completed', 'Terminé')
                      : t('editions.bulk.progress.processing', 'En cours...')}
                  </span>
                  <span className="text-neutral-500">
                    {progress?.completed ?? 0}/
                    {progress?.total ?? folderUuids.length}
                  </span>
                </div>
                <div className="h-3 overflow-hidden rounded-full bg-neutral-200">
                  <div
                    className={`h-full rounded-full transition-all duration-500 ${
                      isCompleted ? 'bg-green-500' : 'bg-violet-500'
                    }`}
                    style={{ width: `${progressPercent}%` }}
                  />
                </div>
                <p className="mt-1 text-xs text-neutral-500">
                  {progressPercent}%
                  {progress && progress.failed > 0 && (
                    <span className="ml-2 text-red-500">
                      ({progress.failed}{' '}
                      {t('editions.bulk.progress.failed', 'erreur(s)')})
                    </span>
                  )}
                </p>
              </div>

              {/* Results list */}
              <div className="space-y-2">
                <h3 className="text-sm font-semibold text-neutral-700">
                  {t('editions.bulk.progress.details', 'Détail par dossier')}
                </h3>
                <div className="max-h-96 space-y-1 overflow-y-auto rounded-lg border border-neutral-200 p-2">
                  {(progress?.results ?? []).map(result => (
                    <div
                      key={result.folderUuid}
                      className="flex items-center gap-3 rounded-lg px-3 py-2 text-sm"
                    >
                      {getStatusIcon(result)}
                      <span className="flex-1 truncate text-neutral-700">
                        {result.folderName}
                      </span>
                      {result.status === 'completed' && (
                        <span className="text-xs text-green-600">
                          {result.successCount}/
                          {(result.successCount ?? 0) +
                            (result.errorCount ?? 0)}
                        </span>
                      )}
                      {result.status === 'failed' && result.error && (
                        <span
                          className="max-w-[200px] truncate text-xs text-red-500"
                          title={result.error}
                        >
                          {result.error}
                        </span>
                      )}
                    </div>
                  ))}
                  {(!progress?.results || progress.results.length === 0) && (
                    <div className="flex items-center justify-center gap-2 py-4 text-sm text-neutral-500">
                      <Loader2 className="h-4 w-4 animate-spin" />
                      {t('editions.bulk.progress.starting', 'Démarrage...')}
                    </div>
                  )}
                </div>
              </div>

              {/* Final summary */}
              {isCompleted && progress && (
                <div
                  className={`rounded-lg border p-4 ${
                    progress.failed === 0
                      ? 'border-green-200 bg-green-50'
                      : 'border-amber-200 bg-amber-50'
                  }`}
                >
                  <div className="flex items-center gap-2">
                    {progress.failed === 0 ? (
                      <CheckCircle2 className="h-5 w-5 text-green-600" />
                    ) : (
                      <AlertCircle className="h-5 w-5 text-amber-600" />
                    )}
                    <h4
                      className={`text-sm font-semibold ${
                        progress.failed === 0
                          ? 'text-green-800'
                          : 'text-amber-800'
                      }`}
                    >
                      {progress.failed === 0
                        ? t(
                            'editions.bulk.result.allSuccess',
                            'Toutes les éditions ont été générées avec succès !'
                          )
                        : t(
                            'editions.bulk.result.partial',
                            'Génération terminée avec des erreurs'
                          )}
                    </h4>
                  </div>
                  <ul className="mt-2 space-y-1 text-sm">
                    <li className="text-green-700">
                      <Check className="mr-1 inline h-3.5 w-3.5" />
                      {t(
                        'editions.bulk.result.successCount',
                        '{{count}} dossier(s) réussi(s)',
                        {
                          count: progress.completed - progress.failed,
                        }
                      )}
                    </li>
                    {progress.failed > 0 && (
                      <li className="text-red-700">
                        <X className="mr-1 inline h-3.5 w-3.5" />
                        {t(
                          'editions.bulk.result.failedCount',
                          '{{count}} dossier(s) en erreur',
                          {
                            count: progress.failed,
                          }
                        )}
                      </li>
                    )}
                  </ul>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="border-t border-neutral-200 p-6">
          <div className="flex gap-3">
            <button
              onClick={handleClose}
              className="flex-1 rounded-lg border border-neutral-300 px-4 py-2.5 text-sm font-medium text-neutral-700 transition-colors hover:bg-neutral-50"
            >
              {isSubmitted && isCompleted
                ? t('common.close', 'Fermer')
                : t('common.cancel', 'Annuler')}
            </button>
            {!isSubmitted && (
              <button
                onClick={handleSubmit}
                disabled={
                  selectedEditions.length === 0 ||
                  folderUuids.length === 0 ||
                  bulkMutation.isPending
                }
                className="flex flex-1 items-center justify-center gap-2 rounded-lg bg-violet-600 px-4 py-2.5 text-sm font-medium text-white transition-colors hover:bg-violet-700 disabled:cursor-not-allowed disabled:opacity-50"
              >
                {bulkMutation.isPending ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <FileText className="h-4 w-4" />
                )}
                {t(
                  'editions.bulk.generate',
                  'Générer et sauvegarder dans la GED'
                )}
              </button>
            )}
          </div>
          {bulkMutation.isError && (
            <p className="mt-2 text-center text-sm text-red-600">
              {(bulkMutation.error as HttpApiError)?.response?.data?.message ||
                (bulkMutation.error as HttpApiError)?.message ||
                t('editions.bulk.error', 'Une erreur est survenue')}
            </p>
          )}
        </div>
      </div>
    </div>
  );
}
